import { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useFormik } from 'formik';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';

import { SubmitButton } from './components/SubmitButton';
import { ErrorMessage } from './components/ErrorMessage';
import { StoreSection } from './components/StoreSection';
import { BirthdateInput } from './components/BirthdateInput';
import { NameSection } from './components/NameSection';
import { AddressSection } from './components/AddressSection';
import { DividerLine } from './components/DividerLine';
import { RelocationDateInput } from './components/RelocationDateInput';
import { RelocationAddressSection } from './components/RelocationAddressSection';
import { breakpoints, initialValues } from './constants';
import { useValidationSchema } from './utils';
import { TFormValues } from './types';

const Footnote = styled.div``;

interface IProps {
    className?: string;
}

const BaseApp = ({ className }: IProps) => {
    const [error, setError] = useState('');
    const {
        t,
        i18n: { language },
    } = useTranslation();

    const formik = useFormik({
        initialValues,
        validationSchema: useValidationSchema(),
        onSubmit: async (formValues) => {
            if (!process.env.REACT_APP_MAILER_API_URL) return;

            const postBody = {
                substitutionData: {
                    ...formValues,
                },
                company: 'pbe',
                useEuAccount: true,
            };

            const isProduction =
                process.env.NODE_ENV === 'production' &&
                process.env.REACT_APP_IS_STAGING !== 'true';

            // Send mail to store(s) only in production build
            const storeEmail = isProduction
                ? formValues.storeEmail
                : process.env.REACT_APP_TEST_EMAIL;

            const storeTwoEmail = isProduction ? formValues.storeTwoEmail : '';

            const customerServiceBody = {
                ...postBody,
                email: storeEmail,
                bcc: storeTwoEmail,
                templateId:
                    language === 'fr'
                        ? 'pbe---verhuisformulier---klantenservice---fr'
                        : 'pbe---verhuisformulier---klantenservice---nl',
            };

            const customerBody = {
                ...postBody,
                email: formValues.customerEmail,
                templateId:
                    language === 'fr'
                        ? 'pbe---verhuisformulier---klant---fr'
                        : 'pbe---verhuisformulier---klant---nl',
            };

            try {
                // Send customer service email
                await axios.post(
                    process.env.REACT_APP_MAILER_API_URL,
                    customerServiceBody
                );

                // Send customer email
                await axios.post(
                    process.env.REACT_APP_MAILER_API_URL,
                    customerBody
                );

                const redirectObj = window.top ?? window;
                redirectObj.location.href =
                    language === 'fr'
                        ? 'https://www.pearle.be/fr_BE/service/merci-demenager'
                        : 'https://www.pearle.be/nl_BE/service/bedankt-verhuizing';
            } catch {
                const errorMsg = t('somethingWentWrong');

                setError(errorMsg);
            }
        },
    });

    const {
        values: {
            customerEmail,
            birthdate,
            relocationDate,
            phoneNumber,
            termsAgreed,
        },
        touched,
        errors: formikErrors,
        handleChange,
        handleSubmit,
        setFieldValue,
    } = formik;

    const getFieldError = (fieldName: keyof TFormValues) => {
        if (touched[fieldName] && formikErrors[fieldName]) {
            return formikErrors[fieldName];
        }

        return '';
    };

    const privacyStatementUrl =
        language === 'fr'
            ? 'https://www.pearle.be/fr_BE/accueil/privacy-policy'
            : 'https://www.pearle.be/nl_BE/home/disclaimer-privacy';

    const conditionsUrl =
        language === 'fr'
            ? 'https://www.pearle.be/fr_BE/accueil/conditions-generales'
            : 'https://www.pearle.be/nl_BE/home/algemene-voorwaarden';

    return (
        <div className={className}>
            <form onSubmit={handleSubmit}>
                <h3>{t('yourInfo')}</h3>
                <NameSection
                    formValues={formik.values}
                    getFieldError={getFieldError}
                    setFieldValue={setFieldValue}
                />
                <BirthdateInput
                    birthdate={birthdate}
                    getFieldError={getFieldError}
                    setFieldValue={setFieldValue}
                />
                <TextField
                    error={!!getFieldError('phoneNumber')}
                    helperText={getFieldError('phoneNumber')}
                    id="phoneNumber"
                    label={t('phoneNumber') + ' *'}
                    value={phoneNumber}
                    onChange={handleChange}
                />
                <TextField
                    error={!!getFieldError('customerEmail')}
                    helperText={getFieldError('customerEmail')}
                    id="customerEmail"
                    label={t('email') + ' *'}
                    type="email"
                    value={customerEmail}
                    onChange={handleChange}
                />
                <h3>{t('yourStores')}</h3>
                <StoreSection
                    getFieldError={getFieldError}
                    setFieldValue={setFieldValue}
                    storeEmailField="storeEmail"
                    storeField="store"
                />
                <StoreSection
                    getFieldError={getFieldError}
                    setFieldValue={setFieldValue}
                    storeEmailField="storeTwoEmail"
                    storeField="storeTwo"
                />
                <h3 style={{ marginTop: '30px' }}>{t('postService')}</h3>
                <FormControlLabel
                    control={
                        <Checkbox
                            id="sendToDeliveryService"
                            onChange={handleChange}
                        />
                    }
                    label={t('yes') || ''}
                />
                <h3>{t('relocationDate')}</h3>
                <RelocationDateInput
                    getFieldError={getFieldError}
                    relocationDate={relocationDate}
                    setFieldValue={setFieldValue}
                />
                <h3>{t('oldAddress')}</h3>
                <AddressSection
                    formValues={formik.values}
                    getFieldError={getFieldError}
                    setFieldValue={setFieldValue}
                />
                <h3>{t('newAddress')}</h3>
                <RelocationAddressSection
                    formValues={formik.values}
                    getFieldError={getFieldError}
                    setFieldValue={setFieldValue}
                />
                <Footnote>* {t('requiredFootnote')}</Footnote>
                <DividerLine />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={termsAgreed}
                            id="termsAgreed"
                            onChange={handleChange}
                        />
                    }
                    label={
                        <Trans i18nKey="disclaimer">
                            Door het invullen van dit formulier ga ik akkoord
                            met de{' '}
                            <a
                                href={conditionsUrl}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                Algemene Voorwaarden
                            </a>{' '}
                            en de{' '}
                            <a
                                href={privacyStatementUrl}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                Privacy Statement
                            </a>
                            .
                        </Trans>
                    }
                />
                {error ? (
                    <ErrorMessage>{error}</ErrorMessage>
                ) : (
                    <SubmitButton disabled={!termsAgreed}>
                        {t('send')}
                    </SubmitButton>
                )}
            </form>
        </div>
    );
};

export const App = styled(BaseApp)`
    ${Footnote} {
        font-size: 14px;
        margin-top: 20px;
    }

    // Styles matching the Pearle environment
    padding: 0 32px 100px;

    @media (${breakpoints.largeMin}) {
        padding: 0 0 100px;
        margin: 0 auto;
        max-width: 920px;
    }
`;
